import {
    BookOpen,
    Brain,
    Calendar,
    ClipboardList,
    Layers,
    type LucideIcon,
    Megaphone,
    Stethoscope,
    User,
} from 'lucide-react';

import { type ReferenceType } from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';
import { ENTITY_TINTS } from '@apps/web/lib/entity_palette';

/** Canonical entity icons shared by draft mentions and transcript badges. */
export const REFERENCE_TYPE_ICONS: Readonly<Record<ReferenceType, LucideIcon>> =
    {
        shift: Calendar,
        // The care-provider's tool, so a caregiver reads apart from the
        // client's plain person icon at pill size.
        caregiver: Stethoscope,
        client: User,
        callout: Megaphone,
        // Stacked sheets: a group is several shifts picked as one.
        group: Layers,
        playbook: BookOpen,
        care_plan: ClipboardList,
        caregiver_memory: Brain,
        client_memory: Brain,
    };

// Shift and callout colors come from the shared entity palette. Caregiver
// and client tints are deliberately v3-local: the v3 chat wears a lighter
// caregiver green and a sky-blue client (distinct from the outreaching
// periwinkle) while the legacy chat keeps the shared palette's colors.

/** Chip tint (background + text) per entity, for mention/reference badges. */
export const REFERENCE_TYPE_TINT_CLASSES: Readonly<
    Record<ReferenceType, string>
> = {
    shift: ENTITY_TINTS.shift.pill,
    caregiver:
        'bg-[#E6FCEF] text-green-950 dark:bg-green-950/70 dark:text-green-50',
    client: 'bg-[#E0F0FF] text-blue-950 dark:bg-blue-950/70 dark:text-blue-50',
    callout: ENTITY_TINTS.callout.pill,
    group: ENTITY_TINTS.group.pill,
    playbook: ENTITY_TINTS.playbook.pill,
    care_plan: ENTITY_TINTS.care_plan.pill,
    caregiver_memory: ENTITY_TINTS.caregiver_memory.pill,
    client_memory: ENTITY_TINTS.client_memory.pill,
};

/** Hover shade for interactive badges; separate so the composer's inert
 * draft chips share the tint without carrying hover styles. */
export const REFERENCE_TYPE_HOVER_CLASSES: Readonly<
    Record<ReferenceType, string>
> = {
    // The status-aware shift palette carries its own hover; this shift
    // entry only covers the static fallback tint.
    shift: 'hover:bg-rose-100',
    caregiver: 'hover:brightness-[0.97]',
    client: 'hover:brightness-[0.97]',
    callout: 'hover:bg-amber-100',
    group: 'hover:bg-amber-100',
    playbook: 'hover:bg-teal-100',
    care_plan: 'hover:bg-rose-100',
    caregiver_memory: 'hover:bg-fuchsia-100',
    client_memory: 'hover:bg-fuchsia-100',
};

/** Icon-only colour per entity, for surfaces that are not chips
 * (the mention picker rows). */
export const REFERENCE_TYPE_ICON_CLASSES: Readonly<
    Record<ReferenceType, string>
> = {
    shift: ENTITY_TINTS.shift.icon,
    caregiver: ENTITY_TINTS.caregiver.icon,
    // Matches the v3-local sky-blue client tint above.
    client: 'text-blue-600',
    callout: ENTITY_TINTS.callout.icon,
    group: ENTITY_TINTS.group.icon,
    playbook: ENTITY_TINTS.playbook.icon,
    care_plan: ENTITY_TINTS.care_plan.icon,
    caregiver_memory: ENTITY_TINTS.caregiver_memory.icon,
    client_memory: ENTITY_TINTS.client_memory.icon,
};

/** The outline that marks a chip as a cited source rather than an entity
 * being discussed. Drawn in the chip's own text colour on its own tint, so
 * it reads the same whatever the surrounding surface is. */
// A cited source is a receipt on the claim before it, so it stays
// quieter than the entity chips and shorter than the line it sits in:
// em-relative size with no vertical padding keeps a cited line exactly
// as tall as an uncited one.
export const SOURCE_CITATION_CLASSES =
    'focus-visible:outline-agent-action bg-muted/70 text-muted-foreground hover:bg-muted hover:text-foreground inline-flex max-w-[11rem] items-center gap-1 rounded-full px-1.5 align-baseline text-[0.8em] leading-[1.35] font-medium no-underline transition-colors duration-[120ms] outline-none focus-visible:outline-2 focus-visible:outline-offset-1';

// A source named inside a sentence is part of the sentence. A dotted rule
// under it marks it as something to open without bolding or boxing the
// words, which would break the line it belongs to.
export const INLINE_SOURCE_CITATION_CLASSES =
    'focus-visible:outline-agent-action decoration-muted-foreground/70 hover:decoration-foreground text-inherit underline decoration-dotted decoration-1 underline-offset-[3px] transition-colors duration-[120ms] outline-none focus-visible:outline-2 focus-visible:outline-offset-1';
